import React, { useState, useEffect } from 'react';
import {
  Card, Button, Badge, Alert, Spinner, Modal, Row, Col, ListGroup
} from 'react-bootstrap';
import {
  Ruler, Plus, Edit, Trash2, Clock, ChevronDown, ChevronRight,
  User, FileText, History
} from 'lucide-react';
import measurementService from '../../../services/measurementService';
import MeasurementForm from './MeasurementForm';

/**
 * ✅ ATELIER: Onglet affichant les fiches de mesures d'une cliente.
 * 
 * - Liste l'historique des fiches (triées par date décroissante)
 * - Permet de créer une nouvelle fiche
 * - Permet d'éditer ou supprimer une fiche existante
 */
const MeasurementsTab = ({ customerId, customerName, isManager }) => {

  const [measurements, setMeasurements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Affichage
  const [expandedId, setExpandedId] = useState(null); // ID de la fiche dépliée

  // Modals
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingMeasurement, setEditingMeasurement] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (customerId) {
      loadMeasurements();
    }
  }, [customerId]);

  const loadMeasurements = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await measurementService.getMeasurementsByCustomer(customerId);

      if (response.success) {
        setMeasurements(response.data || []);
        // Ouvrir automatiquement la fiche la plus récente
        if (response.data && response.data.length > 0) {
          setExpandedId(response.data[0].id);
        }
      } else {
        setError(response.message || 'Erreur lors du chargement');
      }
    } catch (err) {
      console.error('❌ Erreur chargement mesures:', err);
      setError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setEditingMeasurement(null);
    setShowFormModal(true);
  };

  const handleEdit = (measurement) => {
    setEditingMeasurement(measurement);
    setShowFormModal(true);
  };

  const handleFormSuccess = (savedMeasurement) => {
    const wasEditing = !!editingMeasurement;
    setShowFormModal(false);
    setEditingMeasurement(null);

    setSuccess(wasEditing
      ? 'Fiche de mesures mise à jour'
      : 'Nouvelle fiche de mesures créée'
    );
    setTimeout(() => setSuccess(null), 3000);

    loadMeasurements();
  };

  const handleDelete = (measurement) => {
    setDeleteConfirm(measurement);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;

    try {
      setDeleting(true);
      const response = await measurementService.deleteMeasurement(deleteConfirm.id);

      if (response.success) {
        setSuccess('Fiche de mesures supprimée');
        setTimeout(() => setSuccess(null), 3000);
        loadMeasurements();
      } else {
        setError(response.message || 'Erreur lors de la suppression');
      }
    } catch (err) {
      setError('Erreur lors de la suppression');
    } finally {
      setDeleting(false);
      setDeleteConfirm(null);
    }
  };

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  // Rendu d'une fiche de mesures (en mode accordéon)
  const renderMeasurementCard = (measurement, index) => {
    const isExpanded = expandedId === measurement.id;
    const isLatest = index === 0;
    const filledCount = measurementService.countFilledMeasurements(measurement);

    return (
      <Card key={measurement.id} className="mb-3 shadow-sm">
        {/* En-tête cliquable */}
        <Card.Header
          className="d-flex justify-content-between align-items-center"
          style={{ cursor: 'pointer', background: isLatest ? '#f0f9ff' : '#fff' }}
          onClick={() => toggleExpand(measurement.id)}
        >
          <div className="d-flex align-items-center gap-2">
            {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
            <div>
              <div className="fw-semibold">
                Fiche du {measurementService.formatDate(measurement.createdAt)}
                {isLatest && (
                  <Badge bg="primary" className="ms-2">
                    Plus récente
                  </Badge>
                )}
              </div>
              <small className="text-muted d-flex align-items-center gap-2 mt-1">
                <Ruler size={12} />
                {filledCount} mesure{filledCount > 1 ? 's' : ''} renseignée{filledCount > 1 ? 's' : ''}
                {measurement.createdByName && (
                  <>
                    <span className="mx-1">•</span>
                    <User size={12} />
                    {measurement.createdByName}
                  </>
                )}
              </small>
            </div>
          </div>

          {isManager && (
            <div
              className="d-flex gap-1"
              onClick={(e) => e.stopPropagation()}
            >
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => handleEdit(measurement)}
                title="Modifier"
              >
                <Edit size={14} />
              </Button>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => handleDelete(measurement)}
                title="Supprimer"
              >
                <Trash2 size={14} />
              </Button>
            </div>
          )}
        </Card.Header>

        {/* Contenu déplié */}
        {isExpanded && (
          <Card.Body>
            {/* Grille des mesures standards */}
            <Row className="g-2">
              {measurementService.STANDARD_FIELDS.map(field => {
                const value = measurement[field.key];
                const hasValue = value !== null && value !== undefined;
                
                return (
                  <Col key={field.key} xs={6} md={4} lg={3}>
                    <div className={`measurement-cell ${hasValue ? 'filled' : 'empty'}`}>
                      <div className="measurement-label">{field.label}</div>
                      <div className="measurement-value">
                        {hasValue
                          ? measurementService.formatMeasurement(value, field.unit)
                          : '—'
                        }
                      </div>
                    </div>
                  </Col>
                );
              })}
            </Row>

            {/* Mesures personnalisées */}
            {measurement.customMeasurements && 
             Object.keys(measurement.customMeasurements).length > 0 && (
              <div className="mt-4">
                <h6 className="mb-2 d-flex align-items-center gap-2">
                  <Plus size={14} className="text-success" />
                  Mesures personnalisées
                </h6>
                <Row className="g-2">
                  {Object.entries(measurement.customMeasurements).map(([key, value]) => (
                    <Col key={key} xs={6} md={4} lg={3}>
                      <div className="measurement-cell filled custom">
                        <div className="measurement-label">{key}</div>
                        <div className="measurement-value">
                          {measurementService.formatMeasurement(value, 'cm')}
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </div>
            )}

            {/* Notes */}
            {measurement.notes && (
              <div className="mt-4 p-3 bg-light rounded">
                <div className="d-flex align-items-center gap-2 mb-2 text-muted">
                  <FileText size={14} />
                  <small className="fw-semibold">Notes</small>
                </div>
                <div className="small" style={{ whiteSpace: 'pre-wrap' }}>
                  {measurement.notes}
                </div>
              </div>
            )}
          </Card.Body>
        )}
      </Card>
    );
  };

  return (
    <div className="measurements-tab">
      {/* En-tête avec bouton créer */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div>
          <h5 className="mb-1 d-flex align-items-center gap-2">
            <Ruler size={20} className="text-primary" />
            Mesures de {customerName || 'la cliente'}
          </h5>
          <small className="text-muted">
            {measurements.length === 0
              ? 'Aucune fiche enregistrée'
              : `${measurements.length} fiche${measurements.length > 1 ? 's' : ''} dans l'historique`
            }
          </small>
        </div>

        <Button variant="primary" onClick={handleCreateNew}>
          <Plus size={16} className="me-1" />
          Nouvelle fiche
        </Button>
      </div>

      {/* Alertes */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Contenu */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <div className="mt-2 text-muted">Chargement des fiches...</div>
        </div>
      ) : measurements.length === 0 ? (
        <Card className="text-center py-5 shadow-sm">
          <Card.Body>
            <Ruler size={64} className="text-muted mb-3" />
            <h5>Aucune fiche de mesures</h5>
            <p className="text-muted mb-3">
              Cette cliente n'a pas encore de fiche de mesures enregistrée.
            </p>
            <Button variant="primary" onClick={handleCreateNew}>
              <Plus size={16} className="me-1" />
              Créer la première fiche
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <div>
          {measurements.map((m, i) => renderMeasurementCard(m, i))}
        </div>
      )}

      {/* Modal formulaire création/édition */}
      <Modal
        show={showFormModal}
        onHide={() => {
          setShowFormModal(false);
          setEditingMeasurement(null);
        }}
        size="lg"
        centered
        backdrop="static"
      >
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="d-flex align-items-center gap-2">
            <Ruler size={20} className="text-primary" />
            {editingMeasurement
              ? 'Modifier la fiche de mesures'
              : 'Nouvelle fiche de mesures'
            }
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <MeasurementForm
            customerId={customerId}
            existingMeasurement={editingMeasurement}
            onSuccess={handleFormSuccess}
            onCancel={() => {
              setShowFormModal(false);
              setEditingMeasurement(null);
            }}
          />
        </Modal.Body>
      </Modal>

      {/* Modal confirmation suppression */}
      <Modal
        show={!!deleteConfirm}
        onHide={() => setDeleteConfirm(null)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirmer la suppression</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Êtes-vous sûr de vouloir supprimer la fiche de mesures du{' '}
            <strong>
              {deleteConfirm ? measurementService.formatDate(deleteConfirm.createdAt) : ''}
            </strong>{' '}
            ?
          </p>
          <Alert variant="info" className="mb-0">
            <small>
              Cette action est un <strong>soft delete</strong> : la fiche sera
              masquée mais les commandes historiques qui l'utilisent resteront intactes.
            </small>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setDeleteConfirm(null)}
            disabled={deleting}
          >
            Annuler
          </Button>
          <Button
            variant="danger"
            onClick={confirmDelete}
            disabled={deleting}
          >
            {deleting ? (
              <>
                <Spinner size="sm" className="me-2" />
                Suppression...
              </>
            ) : (
              'Supprimer'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .measurement-cell {
          padding: 0.5rem 0.75rem;
          border-radius: 6px;
          border: 1px solid #e2e8f0;
          background: #fff;
          transition: all 0.15s ease;
        }

        .measurement-cell.filled {
          background: #f0f9ff;
          border-color: #bae6fd;
        }

        .measurement-cell.filled.custom {
          background: #f0fdf4;
          border-color: #bbf7d0;
        }

        .measurement-cell.empty {
          background: #f8fafc;
          opacity: 0.6;
        }

        .measurement-label {
          font-size: 0.75rem;
          color: #64748b;
          margin-bottom: 2px;
          font-weight: 500;
        }

        .measurement-value {
          font-size: 0.95rem;
          font-weight: 600;
          color: #1f2937;
        }

        .measurement-cell.empty .measurement-value {
          color: #cbd5e1;
          font-weight: 400;
        }
      `}</style>
    </div>
  );
};

export default MeasurementsTab;